import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Send, 
  Upload, 
  CheckCircle, 
  Home, 
  Users, 
  DollarSign, 
  Calendar,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from "sonner";

const RequestForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    budget: '',
    location: '',
    timeline: '',
    description: '',
    attachments: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Validar campos requeridos
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error("Por favor completa todos los campos requeridos");
        setIsSubmitting(false);
        return;
      }
      
      // Enviar datos al backend
      const response = await fetch(`${BACKEND_URL}/api/quotes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          projectType: formData.projectType || 'No especificado',
          bedrooms: formData.bedrooms || 'N/A',
          bathrooms: formData.bathrooms || 'N/A',
          area: formData.area || 'N/A',
          budget: formData.budget || 'N/A',
          location: formData.location || 'N/A',
          timeline: formData.timeline || 'Normal',
          description: formData.description || 'Sin descripción adicional'
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al enviar la solicitud');
      }
      
      const data = await response.json();
      
      toast.success("¡Solicitud enviada exitosamente!", {
        description: "Te contactaremos pronto. Revisa tu email para la confirmación.",
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        bedrooms: '',
        bathrooms: '',
        area: '',
        budget: '',
        location: '',
        timeline: '',
        description: '',
        attachments: []
      });
      
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al enviar la solicitud", {
        description: "Por favor intenta de nuevo o contáctanos directamente.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: CheckCircle, text: 'Consulta inicial gratuita' },
    { icon: CheckCircle, text: 'Propuesta personalizada' },
    { icon: CheckCircle, text: 'Revisiones incluidas' },
    { icon: CheckCircle, text: 'Entrega garantizada' }
  ];

  return (
    <section id="solicitar" className="py-20 bg-gradient-to-br from-slate-50 via-white to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-red-100 text-red-800 border-red-200 px-4 py-2">
            Solicita Tu Diseño
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Comienza Tu
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-700"> Proyecto</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Cuéntanos sobre tu proyecto soñado y te ayudaremos a hacerlo realidad con un diseño único y personalizado
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-8">
                <CardTitle className="text-2xl text-slate-900 flex items-center gap-3">
                  <Home className="text-red-600" size={28} />
                  Detalles de tu Proyecto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2 text-slate-700 font-medium">
                        <Users size={16} />
                        Nombre Completo *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Juan Pérez"
                        required
                        className="border-slate-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2 text-slate-700 font-medium">
                        <Mail size={16} />
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="juan@email.com"
                        required
                        className="border-slate-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2 text-slate-700 font-medium">
                        <Phone size={16} />
                        Teléfono
                      </Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+1 234 567 8900"
                        className="border-slate-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location" className="flex items-center gap-2 text-slate-700 font-medium">
                        <MapPin size={16} />
                        Ubicación del Proyecto
                      </Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="Ciudad, Estado"
                        className="border-slate-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">Tipo de Proyecto</Label>
                      <Select value={formData.projectType} onValueChange={(value) => handleInputChange('projectType', value)}>
                        <SelectTrigger className="border-slate-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Selecciona tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="diseno-online-basico">Diseño Online Básico (&lt;30 m²)</SelectItem>
                          <SelectItem value="diseno-online-estandar">Diseño Online Estándar (31-90 m²)</SelectItem>
                          <SelectItem value="diseno-online-premium">Diseño Online Premium (91+ m²)</SelectItem>
                          <SelectItem value="asesoria-online">Asesoramiento Online (Videollamada)</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">Habitaciones</Label>
                      <Select value={formData.bedrooms} onValueChange={(value) => handleInputChange('bedrooms', value)}>
                        <SelectTrigger className="border-slate-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Cantidad" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 habitación</SelectItem>
                          <SelectItem value="2">2 habitaciones</SelectItem>
                          <SelectItem value="3">3 habitaciones</SelectItem>
                          <SelectItem value="4">4 habitaciones</SelectItem>
                          <SelectItem value="5+">5+ habitaciones</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">Baños</Label>
                      <Select value={formData.bathrooms} onValueChange={(value) => handleInputChange('bathrooms', value)}>
                        <SelectTrigger className="border-slate-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Cantidad" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 baño</SelectItem>
                          <SelectItem value="2">2 baños</SelectItem>
                          <SelectItem value="3">3 baños</SelectItem>
                          <SelectItem value="4+">4+ baños</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="area" className="text-slate-700 font-medium">Área Aproximada (m²)</Label>
                      <Input
                        id="area"
                        value={formData.area}
                        onChange={(e) => handleInputChange('area', e.target.value)}
                        placeholder="150"
                        className="border-slate-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-700 font-medium">
                        <DollarSign size={16} />
                        Presupuesto
                      </Label>
                      <Select value={formData.budget} onValueChange={(value) => handleInputChange('budget', value)}>
                        <SelectTrigger className="border-slate-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Rango" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-5k">Menos de $5,000</SelectItem>
                          <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                          <SelectItem value="10k-20k">$10,000 - $20,000</SelectItem>
                          <SelectItem value="20k-50k">$20,000 - $50,000</SelectItem>
                          <SelectItem value="over-50k">Más de $50,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-700 font-medium">
                        <Calendar size={16} />
                        Tiempo Deseado
                      </Label>
                      <Select value={formData.timeline} onValueChange={(value) => handleInputChange('timeline', value)}>
                        <SelectTrigger className="border-slate-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Urgencia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgente (1 semana)</SelectItem>
                          <SelectItem value="normal">Normal (2-3 semanas)</SelectItem>
                          <SelectItem value="flexible">Flexible (1 mes+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-slate-700 font-medium">
                      Descripción del Proyecto
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe tu visión, necesidades especiales, estilo preferido, etc."
                      rows={4}
                      className="border-slate-200 focus:border-red-500 focus:ring-red-500 resize-none"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-slate-700 font-medium">
                      <Upload size={16} />
                      Archivos Adjuntos (Opcional)
                    </Label>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-red-400 transition-colors duration-300">
                      <Upload size={48} className="mx-auto text-slate-400 mb-4" />
                      <p className="text-slate-600 mb-2">
                        Arrastra archivos aquí o 
                        <label className="text-red-600 cursor-pointer hover:underline ml-1">
                          selecciona archivos
                          <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf,.dwg"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                        </label>
                      </p>
                      <p className="text-sm text-slate-500">
                        Fotos del terreno, sketches, referencias (JPG, PNG, PDF, DWG)
                      </p>
                      {formData.attachments.length > 0 && (
                        <div className="mt-4 text-left">
                          <p className="text-sm font-medium text-slate-700 mb-2">Archivos seleccionados:</p>
                          {formData.attachments.map((file, index) => (
                            <p key={index} className="text-sm text-red-600">• {file.name}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-semibold transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-red-200"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Enviando Solicitud...
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Send size={20} />
                        Enviar Solicitud de Diseño
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Features */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900">¿Qué Incluye?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <IconComponent size={20} className="text-red-600" />
                      <span className="text-slate-700">{feature.text}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="border-0 shadow-xl bg-red-600 text-white">
              <CardHeader>
                <CardTitle className="text-xl">¿Tienes Preguntas?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-6">
                  Nuestro equipo está listo para ayudarte con tu proyecto. Contáctanos directamente.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} />
                    <span>+1 (505) 226-0487</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} />
                    <span>info@drywall-design.com</span>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  className="w-full mt-6 bg-white text-red-600 hover:bg-red-50"
                >
                  Llamar Ahora
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestForm;